// Copyright (c) Ping. All rights reserved.
//
// The first-run app tour: a lap around the three main tabs, the "All tools"
// menu (with every tool inside it), and the account avatar. Each step
// switches to the tab its target lives in (via the router) before Reactour
// spotlights it, so the user always sees the real surface.

import type { RouterApi } from '@shell/RouterContext';

import type { GuidedStep } from './GuidedTourTypes';

// Open the "All tools" popover if it is not already open. Detected via the
// button's aria-expanded (set from the rail's accessibilityState), so it is
// not confused by a pinned tool that also lives on the rail.
function ensureAllToolsOpen(): void {
  if (typeof document === 'undefined') {
    return;
  }
  const btn = document.querySelector<HTMLElement>('[data-testid="nav-all-tools"]');
  if (btn === null || btn.getAttribute('aria-expanded') === 'true') {
    return;
  }
  btn.click();
}

// Close the "All tools" popover if it is open (used when leaving the tools
// section so the last step's spotlight is not obscured).
function ensureAllToolsClosed(): void {
  if (typeof document === 'undefined') {
    return;
  }
  const btn = document.querySelector<HTMLElement>('[data-testid="nav-all-tools"]');
  if (btn !== null && btn.getAttribute('aria-expanded') === 'true') {
    btn.click();
  }
}

export function buildHomeTourSteps(router: RouterApi): GuidedStep[] {
  const goChat = (): void => router.push('/chat');
  const goCalls = (): void => router.push('/calls');
  const goContacts = (): void => router.push('/contacts');

  return [
    {
      id: 'welcome',
      selector: '[data-testid="nav-account"]',
      title: 'Welcome to Ping',
      body: 'This is your account. Open it anytime for your profile and settings. Let me show you around first.',
      position: 'right',
      onEnter: goChat,
    },
    {
      id: 'chat-tab',
      selector: '[data-testid="nav-chat"]',
      title: 'Your chats',
      body: 'Chat is your home base. Every one to one and group conversation lives here.',
      position: 'right',
      onEnter: goChat,
    },
    {
      id: 'chat-new',
      selector: '[data-testid="chat-new-chat"]',
      title: 'Start something new',
      body: 'Use the plus button to message a contact or start a group.',
      position: 'bottom',
      onEnter: goChat,
    },
    {
      id: 'chat-filters',
      selector: '[data-testid="chat-filters"]',
      title: 'Find a thread fast',
      body: 'Filter between everything, unread, and your favourites without scrolling.',
      position: 'bottom',
      onEnter: goChat,
    },
    {
      id: 'calls-tab',
      selector: '[data-testid="nav-history"]',
      title: 'Calls',
      body: 'Your voice and video call history, all in one place.',
      position: 'right',
      onEnter: goCalls,
    },
    {
      id: 'calls-new',
      selector: '[data-testid="nav-history"]',
      title: 'Call anyone',
      body: 'Start a voice or video call right from here.',
      position: 'bottom',
      onEnter: goCalls,
    },
    {
      id: 'calls-search',
      selector: '[data-testid="calls-search"]',
      title: 'Pick up where you left off',
      body: 'Search past calls by name or company to call back in a tap.',
      position: 'bottom',
      onEnter: goCalls,
    },
    {
      id: 'contacts-tab',
      selector: '[data-testid="nav-contacts"]',
      title: 'Contacts',
      body: 'Everyone you can reach on Ping lives here.',
      position: 'right',
      onEnter: goContacts,
    },
    {
      id: 'contacts-add',
      selector: '[data-testid="contacts-add"]',
      title: 'Grow your circle',
      body: 'Add someone new, or bring in the contacts already on your device.',
      position: 'bottom',
      onEnter: goContacts,
    },
    {
      id: 'contacts-search',
      selector: '[data-testid="contacts-search"]',
      title: 'Search your people',
      body: 'Find anyone by name, company, or title in seconds.',
      position: 'bottom',
      onEnter: goContacts,
    },
    {
      id: 'all-tools',
      selector: '[data-testid="nav-all-tools"]',
      title: 'All tools',
      body: 'Everything else in Ping lives here. Open it to reach any tool, and pin the ones you use most to your sidebar.',
      position: 'right',
      onEnter: ensureAllToolsOpen,
    },
    {
      id: 'tool-directory',
      selector: '[data-testid="nav-tool-directory"]',
      title: 'Directory',
      body: 'Browse the people and companies on Ping to find who you are looking for.',
      position: 'bottom',
      onEnter: ensureAllToolsOpen,
    },
    {
      id: 'tool-jobboard',
      selector: '[data-testid="nav-tool-jobboard"]',
      title: 'Job Board',
      body: 'Discover opportunities and share your own openings.',
      position: 'bottom',
      onEnter: ensureAllToolsOpen,
    },
    {
      id: 'tool-features',
      selector: '[data-testid="nav-tool-features"]',
      title: 'Features',
      body: 'See what is new and explore more of what Ping can do.',
      position: 'bottom',
      onEnter: ensureAllToolsOpen,
    },
    {
      id: 'tool-vault',
      selector: '[data-testid="nav-tool-vault"]',
      title: 'Vault',
      body: 'Keep important files and notes in a private, secure space.',
      position: 'bottom',
      onEnter: ensureAllToolsOpen,
    },
    {
      id: 'tool-events',
      selector: '[data-testid="nav-tool-events"]',
      title: 'Events',
      body: 'Plan get-togethers and join the ones you are invited to.',
      position: 'bottom',
      onEnter: ensureAllToolsOpen,
    },
    {
      id: 'tool-calendar',
      selector: '[data-testid="nav-tool-calendar"]',
      title: 'Calendar',
      body: 'See your schedule and what is coming up at a glance.',
      position: 'bottom',
      onEnter: ensureAllToolsOpen,
    },
    {
      id: 'account',
      selector: '[data-testid="nav-account"]',
      title: 'You are all set',
      body: 'Open your account whenever you want to update your profile or settings. Enjoy Ping.',
      position: 'right',
      onEnter: ensureAllToolsClosed,
    },
  ];
}
